/**
 * AQ-Cloud2 primary sidebar.
 *
 * Three states:
 *   • Expanded (240px) — default at ≥1440px, persisted in localStorage as
 *     `aq.sidebar.collapsed = "0"`.
 *   • Rail (64px)      — at 1024–1439px or when the user pins it collapsed
 *     (`aq.sidebar.collapsed = "1"`).
 *   • Drawer           — owned by AppLayout on <1024px (this file isn't
 *     rendered in that mode; the drawer renders its own internal nav).
 *
 * The visual active-item style is provided by NavItem (3px brand-accent
 * left bar + bold brand label + muted background).
 */
import React from 'react'
import { ChevronLeft, ChevronRight, HelpCircle, Sparkles } from 'lucide-react'
import { NavItem, type NavItemConfig } from '@/components/layout/NavItem'
import { cn } from '@/lib/utils'

export interface NavGroup {
  label: string
  items: NavItemConfig[]
}

interface SidebarProps {
  groups: NavGroup[]
  /** Controlled collapsed state. AppLayout owns persistence. */
  collapsed?: boolean
  /** Controlled toggle handler. */
  onToggleCollapsed?: () => void
  /** Top slot — typically a UserInfoCard. */
  header?: React.ReactNode
  /** Optional override of the bottom action row. */
  footer?: React.ReactNode
  className?: string
}

const HelpButton = ({ collapsed }: { collapsed: boolean }): JSX.Element => (
  <button
    type="button"
    className={cn(
      'inline-flex items-center gap-2 rounded text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-fast',
      collapsed ? 'w-10 h-10 justify-center' : 'h-8 px-2',
    )}
    aria-label="Help"
  >
    <HelpCircle className="h-[18px] w-[18px]" aria-hidden="true" />
    {!collapsed && <span>Help</span>}
  </button>
)

const WhatsNewButton = (): JSX.Element => (
  <button
    type="button"
    className="h-8 px-2 inline-flex items-center gap-1.5 rounded text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-fast"
    aria-label="What's new"
  >
    <Sparkles className="h-[16px] w-[16px]" aria-hidden="true" />
    <span>What&apos;s New</span>
  </button>
)

export const Sidebar = ({
  groups,
  collapsed = false,
  onToggleCollapsed,
  header,
  footer,
  className,
}: SidebarProps): JSX.Element => {
  return (
    <aside
      className={cn(
        'flex flex-col bg-card text-foreground border-r border-border shrink-0 transition-[width] duration-fast',
        collapsed ? 'w-16' : 'w-60',
        className,
      )}
      aria-label="Primary"
    >
      {header && !collapsed && (
        <div className="p-3 border-b border-border">{header}</div>
      )}

      <nav className="flex-1 overflow-y-auto px-3 pb-3 pt-2" aria-label="Sections">
        {groups.map((group, idx) => (
          <div key={group.label} className={cn(idx === 0 ? 'pt-1' : 'pt-3')}>
            {!collapsed && (
              <div className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {group.label}
              </div>
            )}
            <div className={cn(collapsed ? 'flex flex-col gap-1' : 'flex flex-col gap-0.5')}>
              {group.items.map((item) => (
                <NavItem key={item.path} item={item} collapsed={collapsed} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div
        className={cn(
          'border-t border-border p-2',
          collapsed ? 'flex flex-col items-center gap-1' : 'flex items-center justify-between',
        )}
      >
        {footer ?? (
          <>
            <HelpButton collapsed={collapsed} />
            {!collapsed && <WhatsNewButton />}
            {onToggleCollapsed && (
              <button
                type="button"
                onClick={onToggleCollapsed}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                className={cn(
                  'inline-flex items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-fast',
                  collapsed ? 'w-10 h-10' : 'h-8 w-8',
                )}
              >
                {collapsed ? (
                  <ChevronRight className="h-[18px] w-[18px]" aria-hidden="true" />
                ) : (
                  <ChevronLeft className="h-[18px] w-[18px]" aria-hidden="true" />
                )}
              </button>
            )}
          </>
        )}
      </div>
    </aside>
  )
}
